<script setup>
/**
 * A lesson opened in the app (Chris, 2026-10-25): the lesson page's content
 * (PublicReadingPage, `sheet`) over the feed it was opened from, the Lessons
 * tab, its search, or a Mystic's Lessons tab. A /s/ link from outside still
 * gets the full page.
 *
 * The paper is the sheet (lesson-ending DESIGN.md §2, 2026-10-27): no purple
 * frame around the letter and no empty band above it; only the ✕ floats
 * (sticky, never on the picture).
 *
 * Phone (< 768px): a full-height sheet of paper rising from the bottom, the
 * grip on the paper, ✕ in its top corner (a lesson with a picture keeps a
 * slim strip of paper above it for them), closed by ✕, Esc, the backdrop or
 * a downward swipe from its top. From 768px: a centred sheet, 1000 wide at
 * most, below the top-right portrait's band (--tra-clear-top), the feed
 * dimmed behind it.
 *
 * A credit's profile (SeekerModal, z 9000) opens on top; closing it comes
 * back here.
 */
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { hasPicture } from '@/utils/lesson'
import PublicReadingPage from './PublicReadingPage.vue'

const props = defineProps({
  lesson: { type: Object, required: true },
  via: { type: String, default: 'community' },
  // K2: how many lessons this window has opened (1 = the first; ‹ Back from 2).
  depth: { type: Number, default: 1 },
})
const emit = defineEmits(['login', 'draw', 'own', 'leave', 'swap', 'back'])
const canBack = computed(() => props.depth > 1)

// The lesson showing (a related one may have taken its place).
const current = ref(props.lesson)
watch(() => props.lesson, (l) => { current.value = l })
const hasPic = computed(() => hasPicture(current.value))
function onSwap(l) { current.value = l; emit('swap', l) }

const panel = ref(null)
const closeBtn = ref(null)
const drag = ref(0)
let startY = null
function close() { emit('leave') }
function onKey(e) {
  if (e.key !== 'Escape') return
  // The profile over it closes first (it has its own Esc).
  if (document.querySelector('.seeker-modal-overlay')) return
  close()
}
function onTouchStart(e) {
  if (!panel.value || panel.value.scrollTop > 0 || !e.touches || e.touches.length !== 1) { startY = null; return }
  startY = e.touches[0].clientY
}
function onTouchMove(e) {
  if (startY == null) return
  const dy = e.touches[0].clientY - startY
  drag.value = dy > 0 ? dy : 0
}
function onTouchEnd() {
  if (startY == null) return
  const far = drag.value > 110
  startY = null
  drag.value = 0
  if (far) close()
}
let lastFocus = null
onMounted(() => {
  window.addEventListener('keydown', onKey)
  lastFocus = document.activeElement
  if (closeBtn.value) { try { closeBtn.value.focus({ preventScroll: true }) } catch (e) { closeBtn.value.focus() } }
})
onUnmounted(() => {
  window.removeEventListener('keydown', onKey)
  if (lastFocus && lastFocus.focus) { try { lastFocus.focus({ preventScroll: true }) } catch (e) { /* gone */ } }
})
</script>

<template>
  <div class="ls-overlay" data-testid="lesson-sheet" @click.self="close">
    <div ref="panel" class="ls-panel" :class="{ 'has-pic': hasPic }" role="dialog" aria-modal="true" :aria-label="current.lesson || current.question || 'A lesson'"
         :style="drag ? { transform: `translateY(${drag}px)`, transition: 'none' } : null"
         @touchstart.passive="onTouchStart" @touchmove.passive="onTouchMove" @touchend="onTouchEnd" @touchcancel="onTouchEnd">
      <!-- Only the ✕ floats as the letter scrolls; the bar paints nothing, so no
           band slides across the letter (Chris, 2026-10-27). At desktop it sits
           on the cream beside the picture; on a phone a lesson with a picture
           keeps a strip of paper above it for the grip and the ✕. -->
      <div class="ls-bar">
        <button v-if="canBack" type="button" class="ls-back" aria-label="Back to the previous lesson" data-testid="lesson-back" @click="emit('back')">
          <span aria-hidden="true">‹</span><span class="ls-back-t">Back</span>
        </button>
        <button ref="closeBtn" type="button" class="ls-close" aria-label="Close" data-testid="lesson-sheet-close" @click="close">
          <span aria-hidden="true">×</span>
        </button>
      </div>
      <span class="ls-grip" aria-hidden="true"></span>
      <PublicReadingPage :lesson="lesson" :via="via" sheet
                         @login="emit('login')" @draw="(l, ask) => emit('draw', l, ask)" @own="(l, ask) => emit('own', l, ask)"
                         @swap="onSwap" @leave="close" />
    </div>
    <!-- From 1100px the × and ‹ float in the gutter beside the paper, never on the picture. -->
    <div class="ls-gutter" aria-hidden="false">
      <button v-if="canBack" type="button" class="ls-back out" aria-label="Back to the previous lesson" data-testid="lesson-back" @click="emit('back')">
        <span aria-hidden="true">‹</span>
      </button>
      <button type="button" class="ls-close out" aria-label="Close" data-testid="lesson-sheet-close-out" @click="close">
        <span aria-hidden="true">×</span>
      </button>
    </div>
  </div>
</template>

<style scoped>
.ls-overlay {
  position: fixed; inset: 0; z-index: 1100; display: flex; align-items: flex-end; justify-content: center;
  background: rgba(6, 2, 14, .72); backdrop-filter: blur(3px); -webkit-backdrop-filter: blur(3px);
  animation: ls-fade .18s ease-out;
}
.ls-panel {
  position: relative; box-sizing: border-box; width: 100%; max-height: calc(100dvh - 10px); height: calc(100dvh - 10px);
  overflow-y: auto; overflow-x: hidden; overscroll-behavior: contain; -webkit-overflow-scrolling: touch;
  padding: 0; border-radius: 20px 20px 0 0; background: #f4ecdc;
  box-shadow: 0 -20px 60px -20px rgba(0, 0, 0, .9);
  transition: transform .2s ease; animation: ls-rise .24s cubic-bezier(.2, .8, .2, 1);
}
/* Below 1100px a strip of paper above the framed picture holds the grip, ‹ Back and the ✕ (rev 9: every hero is framed). */
.ls-bar { position: sticky; top: 0; z-index: 4; height: 0; pointer-events: none; }
.ls-panel > :deep(.prp) { padding-top: 48px; }
.ls-grip { position: absolute; z-index: 3; top: 8px; left: 50%; width: 40px; height: 4px; margin-left: -20px; border-radius: 2px; background: rgba(42, 24, 69, .22); }
.ls-close {
  position: absolute; top: 6px; right: 6px; width: 44px; height: 44px; border: 0; background: none; padding: 0; cursor: pointer;
  display: grid; place-items: center; pointer-events: auto;
}
.ls-close span {
  width: 34px; height: 34px; border-radius: 50%; display: grid; place-items: center; background: #2a1845;
  box-shadow: 0 4px 14px -4px rgba(0, 0, 0, .7);
  border: 1px solid rgba(231, 201, 131, .3); color: #e8d8ff; font: 500 22px/1 Inter, system-ui, sans-serif;
}
.ls-close:hover span, .ls-back:hover > span:first-child { color: #ffdfa3; }
/* ‹ Back: on the paper strip, top-left (opposite the ×). */
.ls-back {
  position: absolute; top: 6px; left: 6px; display: inline-flex; align-items: center; gap: 6px; height: 44px; padding: 0 12px 0 5px; border: 0; background: none;
  cursor: pointer; pointer-events: auto; font: 600 14px Inter, system-ui, sans-serif; color: #2a1845;
}
.ls-back > span:first-child {
  width: 34px; height: 34px; border-radius: 50%; display: grid; place-items: center; background: #2a1845; color: #e8d8ff; font: 500 22px/1 Inter, system-ui, sans-serif;
  border: 1px solid rgba(231, 201, 131, .3); box-shadow: 0 4px 14px -4px rgba(0, 0, 0, .7);
}
.ls-back:focus-visible { outline: 2px solid #e7c983; outline-offset: -2px; border-radius: 22px; }
.ls-gutter { display: none; }
.ls-close:focus-visible { outline: 2px solid #e7c983; outline-offset: -2px; border-radius: 50%; }
@media (min-width: 768px) {
  .ls-overlay { align-items: flex-start; padding: var(--tra-clear-top, 60px) 24px 40px; overflow-y: auto; }
  .ls-panel {
    width: min(1000px, 100%); height: auto; max-height: none; overflow: visible; border-radius: 20px;
    box-shadow: 0 30px 80px -24px rgba(0, 0, 0, .95); animation: ls-pop .2s ease-out;
  }
  /* The paper's corners: its first and last children are clipped to them. */
  .ls-panel > :deep(.prp) { border-radius: 20px; overflow: hidden; }
  .ls-grip { display: none; }
  .ls-close { right: 10px; top: 10px; }
}
/* From 1100px: the × and ‹ leave the paper for the gutter, fixed beside it. */
@media (min-width: 1100px) {
  .ls-bar { display: none; }
  .ls-panel > :deep(.prp) { padding-top: 0; }
  .ls-gutter { display: block; }
  .ls-close.out, .ls-back.out { position: fixed; z-index: 1101; top: calc(var(--tra-clear-top, 60px) + 2px); }
  .ls-close.out { left: calc(50% + 500px + 10px); right: auto; }
  .ls-back.out { left: calc(50% - 500px - 54px); padding: 0; }
}
@keyframes ls-fade { from { opacity: 0; } to { opacity: 1; } }
@keyframes ls-rise { from { transform: translateY(40px); opacity: .4; } to { transform: none; opacity: 1; } }
@keyframes ls-pop { from { transform: translateY(12px); opacity: 0; } to { transform: none; opacity: 1; } }
@media (prefers-reduced-motion: reduce) { .ls-overlay, .ls-panel { animation: none; } }
</style>
